/**
 * NEEDS ATTENTION — the board-wide list (Metrics Layer A, mock 04; calm-fill B).
 *
 * Opened from the board toolbar, it reads the `needsAttention()` engine and shows
 * every flagged card. The engine owns the classification and the numbers; the copy
 * and the GROUPING live HERE (so a test asserts facts, not prose — see
 * src/needs-attention.ts).
 *
 * CALM-FILL B (Vinnie, 2 Nov 2026 — three AskUserQuestion decisions): the panel
 * FILLS a wide monitor (`.cap-fill`) and flows its rows into columns (the floor is
 * the only lever, in `.attn-card`), but the CARD was re-tuned for calm density at
 * width:
 *   · ONE tight cause phrase per row — `lead fact · qualifier`, number first, the
 *     long explanatory tail dropped (the family header already says why). The inline
 *     red "Overdue / Blocked" pill is gone: the family tone and the words carry it.
 *   · ONE affordance, `Fix →`, that routes to the engine's own `item.fix` — opening
 *     the card straight onto the field that needs the change, not just opening it.
 *     Mute is preserved but fades in on row hover, so the resting panel stays calm.
 *   · The engine's five categories fold into the THREE families the approved target
 *     names — Hurting the schedule / Waiting too long / Missing data — as a thin
 *     presentation layer. The engine and its facts are unchanged; only families with
 *     a live count render, so no empty group is ever invented.
 * Read-only: nothing changes a card until the person acts; anything expected can be
 * muted (per board, in the browser — a quiet local preference, not shared state).
 */
import { useMemo, useState } from 'react';
import Icon, { type IconName } from './Icon';
import Expander from './Expander';
import { shortDay } from '../when';
import type { Issue } from '../types';
import type {
  AttentionItem,
  AttnCategory,
  AttnFix,
  NeedsAttentionResult,
} from '../needs-attention';

/** The three families the panel groups by — the approved presentation taxonomy.
 *  The engine's five categories fold into these; the engine never sees them. */
type Family = 'schedule' | 'waiting' | 'missing';

/** Which family each engine category belongs to (mock D). Behind-plan and the
 *  undated/unlinked "hurting-schedule" both slip or hide the finish date; stuck is
 *  waiting; missing-basics and field-misuse are both a data gap. */
const FAMILY_OF: Record<AttnCategory, Family> = {
  'behind-plan': 'schedule',
  'hurting-schedule': 'schedule',
  stuck: 'waiting',
  'missing-basics': 'missing',
  'field-misuse': 'missing',
};

const FAMILY_ORDER: Family[] = ['schedule', 'waiting', 'missing'];

/** The family header — icon, title, and the plain "why these are here" line. */
const FAMILY_META: Record<Family, { icon: IconName; title: string; why: string }> = {
  schedule: { icon: 'calendar', title: 'Hurting the schedule', why: 'slipping the finish date, or invisible to the plan' },
  waiting: { icon: 'clock', title: 'Waiting too long', why: 'sat or blocked longer than is normal for this board' },
  missing: { icon: 'person', title: 'Missing data', why: 'committed work without the basics, or a value on the wrong field' },
};

/** A worded day like "16 Nov" from a YYYY-MM-DD — the app's own day-month form
 *  (`shortDay`), so Needs Attention reads a date exactly as the board, backlog and
 *  card modal do. Never `toLocaleDateString` with an unset locale, which drifts the
 *  day/month order per machine (R16, and the shared-date lesson that gave us asDate). */
const day = (iso?: string): string => (iso ? shortDay(iso) : '');

const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;

/**
 * The row's ONE cause phrase — `lead fact · qualifier`, number first, composed from
 * the engine's facts (and the issue for the details the engine keeps on the card,
 * never re-derived). A plain string: the whole row reads calm, nothing bold.
 * Nothing the engine knows is dropped — each phrase still names the late-by / idle /
 * no-dates / no-owner fact it was raised for.
 */
function causePhrase(item: AttentionItem, issue: Issue | undefined): string {
  const f = item.facts;
  switch (item.cause) {
    case 'critical-overdue':
      return `On the critical path · ${plural(f.overdueWorkingDays ?? 0, 'working day')} late`;
    case 'overdue':
      return `${plural(f.overdueWorkingDays ?? 0, 'working day')} late · still open`;
    case 'due-after-phase':
      return f.phaseName ? `Due ${day(f.dueDate)} · past the ${f.phaseName} phase` : `Due ${day(f.dueDate)} · past its phase`;
    case 'start-at-risk':
      // The card keeps its own start date; the engine flags the risk, so name both.
      return issue && issue.startsOn ? `Starts ${day(issue.startsOn)} · predecessor still open` : 'Predecessor still open · may start late';
    case 'stuck-blocked':
      if (f.blockedDays == null) return 'Blocked · waiting on something else';
      // "usual clear is N" only when the engine measured this is longer than that
      // board norm — a freshly-blocked card just states how long, no false compare.
      return f.longerThanTypical && f.typicalClearDays != null
        ? `Blocked ${plural(f.blockedDays, 'day')} · usual clear is ${f.typicalClearDays}`
        : `Blocked ${plural(f.blockedDays, 'day')}`;
    case 'stuck-aged':
      // "In flight" is the app's umbrella for in-progress/review work and matches the
      // card-face chip, so one aged card never reads two names for its state (R13).
      return `In flight ${plural(f.agedDays ?? 0, 'day')} · longer than usual here`;
    case 'undated-unlinked': {
      const what = f.missingStart && f.missingDue ? 'No start or due date' : f.missingStart ? 'No start date' : 'No due date';
      return `${what} · off the schedule${issue?.moscow === 'must' ? ' · must-have' : ''}`;
    }
    case 'undated': {
      const what = f.missingStart && f.missingDue ? 'No start or due date' : f.missingStart ? 'No start date' : 'No due date';
      return `${what} · can't be scheduled${issue?.moscow === 'must' ? ' · must-have' : ''}`;
    }
    case 'misfiled-epic':
      return `Epic looks like a ${f.looksLike ?? 'status'}`;
    case 'misfiled-area':
      return `Area looks like a ${f.looksLike ?? 'value'}`;
    case 'no-owner':
      return 'No owner · must-have';
    case 'no-sprint':
      return 'No sprint · must-have';
    case 'no-size':
      return 'No size · not in the burndown';
    default:
      return '';
  }
}

export const muteKey = (item: Pick<AttentionItem, 'ref' | 'cause'>) => `${item.ref}|${item.cause}`;

export interface NeedsAttentionPanelProps {
  result: NeedsAttentionResult;
  issueByRef: Map<string, Issue>;
  sprintNameById: (id: string | null | undefined) => string | undefined;
  muted: Set<string>;
  onToggleMute: (item: AttentionItem) => void;
  /** Open the card for a row. `fix` carries the engine's one-click target so the
   *  card can land straight on the field that needs changing (set-dates → the date
   *  editor, assign → the owner field…), not merely open. */
  onOpenCard: (ref: string, fix?: AttnFix) => void;
  onClose: () => void;
}

export default function NeedsAttentionPanel({
  result, issueByRef, sprintNameById, muted, onToggleMute, onOpenCard, onClose,
}: NeedsAttentionPanelProps) {
  const [filter, setFilter] = useState<Family | 'all'>('all');
  const [showMuted, setShowMuted] = useState(false);
  void sprintNameById; // kept in the prop contract; the calm phrase no longer needs it

  // Split live vs muted so the active list reads clean but a muted flag is one
  // click from coming back (nothing is lost, just quieted).
  const live = useMemo(() => result.items.filter((it) => !muted.has(muteKey(it))), [result.items, muted]);
  const mutedItems = useMemo(() => result.items.filter((it) => muted.has(muteKey(it))), [result.items, muted]);

  // Live counts per FAMILY (the engine's categories folded in). Only families with a
  // live count render — no empty group is invented.
  const familyCounts = useMemo(() => {
    const c = { schedule: 0, waiting: 0, missing: 0 } as Record<Family, number>;
    for (const it of live) c[FAMILY_OF[it.category]] += 1;
    return c;
  }, [live]);

  const total = live.length;
  // When the family you are filtered to empties — you muted its last row, or the last
  // one resolved — its chip is removed, so a `filter` still pinned to it would show a
  // blank body with no chip lit and no way back. Fall the view back to "All" the moment
  // that happens, so the panel never dead-ends (it does not touch the stored choice, so
  // re-raising a flag in that family re-selects it).
  const activeFilter: Family | 'all' = filter !== 'all' && familyCounts[filter] === 0 ? 'all' : filter;
  const shownFamilies = FAMILY_ORDER.filter((fam) => familyCounts[fam] > 0 && (activeFilter === 'all' || activeFilter === fam));

  const renderRow = (item: AttentionItem, isMuted: boolean) => {
    const issue = issueByRef.get(item.ref);
    return (
      <div className={`attn-row${isMuted ? ' is-muted' : ''}`} key={muteKey(item)}>
        <span className="ref">{item.ref}</span>
        <div className="main">
          <div className="ct">{item.title}</div>
          <div className="cause">{causePhrase(item, issue)}</div>
        </div>
        <div className="meta">
          <button
            type="button"
            className="attn-mute"
            onClick={() => onToggleMute(item)}
            // At rest the mute control is hidden (calm); it fades in on row hover/
            // focus-within via CSS. In the muted tray it is always shown so a flag
            // can be brought back.
          >
            {isMuted ? 'Unmute' : 'Mute'}
          </button>
          <button type="button" className="attn-fix" onClick={() => onOpenCard(item.ref, item.fix)}>
            Fix <Icon name="arrow-right" size={13} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="attn-panel cap-fill">
      <div className="attn-head">
        <div>
          <div className="attn-title">Needs Attention <span className="ct">{total === 1 ? '1 flag' : `${total} flags`}</span></div>
          <div className="attn-sub">
            {total === 0
              ? 'Nothing on this board needs a look right now.'
              : 'Grouped by what it needs. Nothing here changes a card until you act.'}
          </div>
        </div>
        <button type="button" className="attn-close" onClick={onClose} aria-label="Close">
          <Icon name="close" size={18} />
        </button>
      </div>

      {total > 0 && (
        <div className="attn-filters" role="tablist" aria-label="Filter by what it needs">
          <button type="button" className={`attn-cat${activeFilter === 'all' ? ' on' : ''}`} role="tab" aria-selected={activeFilter === 'all'} onClick={() => setFilter('all')}>
            All <span className="n">{total}</span>
          </button>
          {FAMILY_ORDER.filter((fam) => familyCounts[fam] > 0).map((fam) => (
            <button type="button" key={fam} className={`attn-cat${activeFilter === fam ? ' on' : ''}`} role="tab" aria-selected={activeFilter === fam} onClick={() => setFilter(fam)}>
              {FAMILY_META[fam].title} <span className="n">{familyCounts[fam]}</span>
            </button>
          ))}
        </div>
      )}

      {shownFamilies.map((fam) => {
        const meta = FAMILY_META[fam];
        const rows = live.filter((it) => FAMILY_OF[it.category] === fam);
        return (
          <div className="attn-group" key={fam}>
            <div className="attn-gh">
              <span className="gi"><Icon name={meta.icon} size={16} /></span>
              <span className="gt">{meta.title}</span>
              <span className="gw">· {meta.why}</span>
              <span className="gn">{rows.length}</span>
            </div>
            <div className="attn-card">{rows.map((it) => renderRow(it, false))}</div>
          </div>
        );
      })}

      {mutedItems.length > 0 && (
        <>
          <button type="button" className="attn-muted-toggle" onClick={() => setShowMuted((v) => !v)}>
            {showMuted ? 'Hide' : 'Show'} {plural(mutedItems.length, 'muted flag')}
          </button>
          <Expander open={showMuted}>
            <div className="attn-card attn-muted">{mutedItems.map((it) => renderRow(it, true))}</div>
          </Expander>
        </>
      )}
    </div>
  );
}
